<template lang="pug" src="./commission-systems-editor.pug"></template>
<style lang="scss" src="./commission-systems-editor.scss" scoped></style>

<script>
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, numeric } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';

import request from '@/utils/request';
import debounce from "debounce";

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        commissionSystem: {
            name: null,
            period: null,
            brandId: null,
            drivers: null,
            startCalcDate: null,
            onlyForcedOrder: null
        },
        startDateModal: false,
        steps: [],
        step: {
            commissionId: null,
            count: null
        },
        canSaveCommissionSystem: false,
        loaded: false,
        removeModal: false,
        transferModal: false,
        requestProcessing: false,
        commissionSearch: '',
        transferCommissionSystemId: null
    }),
    validations: {
        commissionSystem: {
            name: {
                required
            },
            period: {
                required,
                numeric,
                min: (val) => val >= 1
            },
            brandId: {
                required
            }
        },
        transferCommissionSystemId: {
            required
        }
    },
    computed: {
        ...mapGetters(['userBrandId']),
        ...mapState({
            brands: state => state.data.brands,
            commissions: state => state.data.driverCommissions,
            commissionSystems: function(state) {
                const commissionSystems = state.data.driverCommissionSystems;

                if (!commissionSystems.length || !this.commissionSystem.brandId) {
                    return [];
                }

                return commissionSystems.filter(it => it.id != this.commissionSystem.id);
            }
        }),
        saveMethod() {
            return this.commissionSystem.id ? 'put' : 'post';
        },
        saveUrl() {
            const url = '/commission-systems';

            return this.commissionSystem.id ? `${url}/${this.commissionSystem.id}` : url;
        },
        hasDrivers() {
            return this.commissionSystem.drivers && parseInt(this.commissionSystem.drivers) > 0;
        },
        headers() {
            return ['count', 'commissionId', 'actions'].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.CommissionSystems.stepsData.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        canAddStep() {
            const existStep = this.steps.find(it => it.count == this.step.count);

            return this.step.count && this.step.count > 0 && this.step.commissionId &&
                (!existStep || (existStep && existStep.id == this.step.id));
        },
        formIcon() {
            return this.step.id ? 'done_outline' : 'add';
        }
    },
    watch: {
        commissionSystem: {
            handler: function() {
                this.canSaveCommissionSystem = true;
            },
            deep: true
        },
        commissionSearch: debounce(function(newValue, oldValue) {
            if (typeof newValue != 'string' || !!(newValue && !oldValue)) {
                return;
            }

            this.loadDriverCommissions(newValue);
        }, 500)
    },
    created() {
        if (this.userBrandId) {
            this.commissionSystem.brandId = this.userBrandId;
        }

        if (this.$route.params.id) {
            this.loaded = true;

            this.loadData().then(() => {
                this.loadDriverCommissions();
                this.$store.dispatch('data/getDriverCommissionSystems', {
                    brandId: this.commissionSystem.brandId
                });
            });
        }

        this.$store.dispatch('data/getBrands');
    },
    methods: {
        async loadData() {
            await request({ method: 'get', url: `/commission-systems/${this.$route.params.id}` })
                .then(res => this.successLoad(res))
                .catch(this.errorHandler);
        },

        loadDriverCommissions(query) {
            this.$store.dispatch('data/getDriverCommissions', {
                query: query || '',
                brandIds: JSON.stringify([this.commissionSystem.brandId])
            });
        },

        onCommissionChange(commissionId) {
            this.$set(this.step, 'commissionId', commissionId);
        },

        onBrandChange(brandId) {
            this.$set(this.commissionSystem, 'brandId', brandId);
            this.loadDriverCommissions();
        },

        async saveCommissionSystem() {
            if (this.$v.commissionSystem.$invalid) {
                this.$v.commissionSystem.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign({}, this.commissionSystem);

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    if (res.data) {
                        this.successLoad(res);

                        this.$store.dispatch('alert/addMessage', {
                            type: 'success',
                            message: 'success'
                        });

                        if (!this.$route.params.id) {
                            this.$router.push(`/finances/commission-systems/editor/${this.commissionSystem.id}`);
                        }
                    }
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: this.$vuetify.lang.t(`$vuetify.pages.CommissionSystems.errors.${error.response.data.error}`),
                            locale: false
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        successLoad(res) {
            this.commissionSystem = Object.assign({}, this.commissionSystem, res.data);
            this.steps = JSON.parse(JSON.stringify((res.data.steps || []).sort((a, b) => a.count - b.count)));

            this.$nextTick(() => {
                this.canSaveCommissionSystem = false;
                this.loaded = false;
            });
        },

        async remove() {
            if (this.requestProcessing) return;

            this.requestProcessing = true;

            await request({ method: 'delete', url: `/commission-systems/${this.commissionSystem.id}` })
                .then(res => {
                    if (res.status == 204) {
                        this.$router.push(`/finances/commission-systems`);
                    }
                }).catch(this.errorHandler).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                        this.removeModal = false;
                    });
                });
        },

        checkEditedRow(id) {
            return id == this.step.id;
        },

        editStep(item) {
            this.step = { ...item };
        },

        cancelEdit() {
            this.step = {};
        },

        async removeStep(id) {
            await request({
                method: 'delete',
                url: `/commission-systems/${this.commissionSystem.id}/steps/${id}`
            }).then(res => {
                if (res.status == 204) {
                    const index = this.steps.findIndex(it => it.id == id);

                    if (index != -1) {
                        this.steps.splice(index, 1);
                    }

                    if (id == this.step.id) {
                        this.step = {};
                    }

                    this.$store.dispatch('alert/addMessage', {
                        message: 'success',
                        type: 'success'
                    });
                }
            }).catch(this.errorHandler);
        },

        async saveStep() {
            const method = this.step.id ? 'put' : 'post';
            const path = `/commission-systems/${this.commissionSystem.id}/steps`;
            const url = this.step.id ? `${path}/${this.step.id}` : path;

            await request({
                method,
                url,
                data: { ...this.step }
            }).then(res => {
                if (!this.step.id) {
                    this.steps.push(res.data);
                } else {
                    const index = this.steps.findIndex(it => it.id == this.step.id);

                    if (index != -1) {
                        this.$set(this.steps, index, res.data);
                    }
                }

                this.steps.sort((a, b) => a.count - b.count);

                this.step = {};

                this.$store.dispatch('alert/addMessage', {
                    message: 'success',
                    type: 'success'
                });
            }).catch(this.errorHandler);
        },

        openTransferDialog() {
            if (this.$v.transferCommissionSystemId.$invalid) {
                this.$v.transferCommissionSystemId.$touch();
                return;
            }

            this.transferModal = true;
        },

        async transferDrivers() {
            await request({
                method: 'put',
                url: `/commission-system/assign/${this.commissionSystem.id}/${this.transferCommissionSystemId}`
            }).then(res => {
                if (res.status == 204) {
                    this.$store.dispatch('alert/addMessage', {
                        message: 'success',
                        type: 'success'
                    });

                    this.transferCommissionSystemId = null;
                    this.$set(this.commissionSystem, 'drivers', 0);

                    this.$nextTick(() => {
                        this.transferModal = false;
                        this.$v.transferCommissionSystemId.$reset();
                    });
                }
            }).catch(error => this.errorHandler(error));
        },

        errorHandler(error) {
            const message = error.response && error.response.data && error.response.data.error;

            if (message) {
                this.$store.dispatch('alert/addMessage', {
                    message
                });
            }
        }
    }
};
</script>
